import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import Layout from "@/components/Layout";
import UpgradePrompt from "@/components/UpgradePrompt";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, Trophy, AlertTriangle, Users, Mail, Crown, FileText, Sparkles, ShoppingBag, Trash2 } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Trophy },
  { id: "entries", label: "Entries", icon: Users },
  { id: "leaderboard", label: "Leaderboard", icon: Crown },
  { id: "referrals", label: "Referrals", icon: Users },
  { id: "fraud", label: "Fraud Review", icon: AlertTriangle },
  { id: "rules", label: "Official Rules", icon: FileText },
  { id: "email", label: "Entry Email", icon: Mail },
  { id: "draw", label: "Draw Winner", icon: Sparkles },
];

export default function GiveawayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [cc, setCc] = useState(null);
  const [tab, setTab] = useState("overview");
  const [entries, setEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [fraud, setFraud] = useState(null);
  const [rules, setRules] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [winner, setWinner] = useState(null);
  const [reelName, setReelName] = useState("—");
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailVars, setEmailVars] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const isPro = ["pro", "agency"].includes(user?.plan);
  const proLocked = !isPro && !g?.is_demo;

  const load = async () => {
    const { data } = await api.get(`/giveaways/${id}`);
    setG(data);
    if (data.winner_email) setWinner({ winner_email: data.winner_email, winner_name: data.winner_email.split("@")[0] });
    try { const { data: c } = await api.get(`/giveaways/${id}/command-center`); setCc(c); } catch {}
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (tab === "entries") api.get(`/giveaways/${id}/entries`).then((r) => setEntries(r.data));
    if (tab === "leaderboard") api.get(`/giveaways/${id}/leaderboard`).then((r) => setLeaderboard(r.data));
    if (tab === "referrals") api.get(`/giveaways/${id}/referral/stats`).then((r) => setReferrals(r.data));
    if (tab === "rules") api.get(`/giveaways/${id}/rules`).then((r) => setRules(r.data.rules));
    if (tab === "fraud") api.get(`/giveaways/${id}/fraud`).then((r) => setFraud(r.data));
    if (tab === "email") api.get(`/giveaways/${id}/entry-email`).then((r) => { setEmailCfg(r.data.settings); setEmailVars(r.data.variables); });
  }, [tab, id]);

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.put(`/giveaways/${id}/entry-email`, emailCfg);
      toast.success("Entry email saved");
    } catch { toast.error("Could not save"); }
    finally { setSavingEmail(false); }
  };
  const previewEmail = async () => {
    try { const { data } = await api.post(`/giveaways/${id}/entry-email/preview`); setEmailPreview(data); }
    catch { toast.error("Preview failed"); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a test email address");
    try {
      const { data } = await api.post(`/giveaways/${id}/entry-email/test`, { to_email: testTo });
      toast.success(`Test email ${data.delivery_status}${data.provider === "mock" ? " (mock — set RESEND_API_KEY to deliver)" : ""}`);
    } catch { toast.error("Test send failed"); }
  };

  const addAmoe = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post(`/giveaways/${id}/entries`, {
        email: fd.get("email"),
        name: fd.get("name"),
        source: "amoe",
      });
      toast.success(`Entry added · Confirmation ${data.confirmation_id || ""}`);
      e.target.reset();
      const r = await api.get(`/giveaways/${id}/entries`);
      setEntries(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const simulateOrder = async () => {
    const email = prompt("Customer email?");
    if (!email) return;
    const amount = parseFloat(prompt("Order amount ($)?", "75") || "0");
    if (!amount) return;
    try {
      await api.post(`/shopify/simulate-order`, { giveaway_id: id, email, amount });
      toast.success(`Order tracked - ${email}`);
      const r = await api.get(`/giveaways/${id}/entries`);
      setEntries(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const drawWinner = async () => {
    setDrawing(true); setWinner(null);
    try {
      const { data: entryList } = await api.get(`/giveaways/${id}/entries`);
      const names = entryList.map((e) => e.name || e.email.split("@")[0]);
      if (names.length === 0) { toast.error("No entries to draw from"); setDrawing(false); return; }
      let i = 0;
      const interval = setInterval(() => { setReelName(names[i % names.length]); i++; }, 80);
      const { data } = await api.post(`/giveaways/${id}/draw`);
      setTimeout(() => {
        clearInterval(interval);
        setReelName(data.winner_name);
        setWinner(data);
        setDrawing(false);
        load();
      }, 2800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draw failed");
      setDrawing(false);
    }
  };

  const exportCsv = async () => {
    window.location.href = `${API}/giveaways/${id}/export`;
  };

  const remove = async () => {
    if (!window.confirm("Delete this giveaway and all entries?")) return;
    await api.delete(`/giveaways/${id}`);
    toast.success("Deleted");
    navigate("/giveaways");
  };

  if (!g) return <Layout><div className="p-10 text-zinc-500 text-sm">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-8 py-10" data-testid="giveaway-detail">
        <button onClick={() => navigate("/giveaways")} className="text-xs text-zinc-500 hover:text-zinc-950" data-testid="back-button">← Back</button>
        <div className="flex items-end justify-between mt-3 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-4xl font-bold tracking-tighter">{g.title}</h1>
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded ${
                g.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"
              }`}>
                <span className={`size-1.5 rounded-full ${g.status === "active" ? "bg-emerald-500" : "bg-zinc-400"}`} />
                {g.status}
              </span>
            </div>
            <p className="text-zinc-600 mt-2 max-w-2xl">{g.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} data-testid="export-csv-button" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50 flex items-center gap-2">
              <Download className="size-3.5" strokeWidth={1.5} /> Export CSV
            </button>
            <button onClick={remove} data-testid="delete-giveaway-button" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-red-50 hover:text-red-600 hover:border-red-200 flex items-center gap-2">
              <Trash2 className="size-3.5" strokeWidth={1.5} /> Delete
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200 mb-8 flex gap-1 flex-wrap overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"
              }`}
            >
              <t.icon className="size-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-6">
            {cc && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-zinc-200 border border-zinc-200 rounded-md overflow-hidden" data-testid="command-center">
                {[
                  ["Prize Value", `$${cc.prize_value.toLocaleString()}`],
                  ["Revenue", `$${cc.revenue_generated.toLocaleString()}`],
                  ["Multiplier", `${cc.multiplier}x`],
                  ["Entries", cc.total_entries.toLocaleString()],
                  ["Days Left", cc.days_remaining ?? "—"],
                  ["Top Referrer", cc.top_referrer_email ? cc.top_referrer_email.split("@")[0] : "—"],
                  ["Health", cc.health_status],
                  ["Projected", `$${cc.projected_final_revenue.toLocaleString()}`],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white p-3">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{k}</div>
                    <div className={`font-display text-base font-bold tabular mt-1 ${k === "Health" && cc.health_color === "emerald" ? "text-emerald-600" : ""}`}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6">
              <h3 className="font-display font-bold text-lg">Public Giveaway Link</h3>
              <p className="text-xs text-zinc-500 mt-1">Share this link to collect entries. Each entrant gets a unique referral URL.</p>
              <div className="mt-3 flex gap-2">
                <input readOnly value={`${window.location.origin}/g/${id}`} className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-xs font-mono bg-zinc-50" data-testid="public-link-input" />
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/g/${id}`); toast.success("Copied!"); }} data-testid="copy-public-link" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">Copy</button>
                <a href={`/g/${id}`} target="_blank" rel="noreferrer" data-testid="preview-public-link" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">Preview</a>
              </div>

              <div className="border-t border-zinc-100 mt-6 pt-6">
                <h3 className="font-display font-bold text-lg">Add AMOE Entry</h3>
                <p className="text-xs text-zinc-500 mt-1">Alternate Method of Entry — stays compliant. Confirmation email sent automatically.</p>
              {proLocked ? (
                <div className="mt-4">
                  <UpgradePrompt
                    feature="AMOE Portal"
                    description="Collect free, no-purchase entries and stay legally compliant. Available on Pro and above."
                    compact
                    testid="amoe-upgrade-prompt"
                  />
                </div>
              ) : (
              <form onSubmit={addAmoe} className="mt-4 grid sm:grid-cols-3 gap-3">
                <input name="email" type="email" placeholder="email@example.com" required data-testid="amoe-email-input" className="border border-zinc-200 rounded-md px-3 py-2 text-sm sm:col-span-2 focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none" />
                <input name="name" placeholder="Name (optional)" data-testid="amoe-name-input" className="border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none" />
                <button type="submit" data-testid="amoe-submit-button" className="bg-zinc-950 text-white rounded-md text-sm font-medium hover:bg-zinc-800 px-4 py-2 sm:col-span-3">Add Entry</button>
              </form>
              )}
              </div>

              <div className="border-t border-zinc-100 mt-6 pt-6">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><ShoppingBag className="size-4" strokeWidth={1.5} /> Simulate Shopify Order</h3>
                <p className="text-xs text-zinc-500 mt-1">Test purchase-to-entry conversion. Each order generates entries based on your ratio.</p>
                <button onClick={simulateOrder} data-testid="simulate-order-button" className="mt-3 border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">
                  + Simulate order
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 rounded-md p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Prize Value</div>
                <div className="font-display text-3xl font-bold mt-2 tabular">${g.prize_value.toLocaleString()}</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-md p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Entry Ratio</div>
                <div className="font-display text-2xl font-bold mt-2 tabular">${g.entry_ratio_dollars} = {g.entry_ratio_entries}</div>
                <div className="text-xs text-zinc-500 mt-1">entries per dollar spent</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-md p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Period</div>
                <div className="text-sm mt-2">{g.start_date} → {g.end_date}</div>
              </div>
            </div>
          </div>
          </div>
        )}

        {tab === "entries" && (
          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Email", "Name", "Source", "Entries", "Purchase $", "Fraud", "Email Sent", "Added"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && <tr><td colSpan="8" className="px-5 py-10 text-center text-zinc-500 text-sm">No entries yet</td></tr>}
                {entries.map((e) => (
                  <tr key={e.entry_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`entry-row-${e.entry_id}`}>
                    <td className="px-5 py-3 flex items-center gap-2"><Mail className="size-3.5 text-zinc-400" strokeWidth={1.5} />{e.email}</td>
                    <td className="px-5 py-3">{e.name || "—"}</td>
                    <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded bg-zinc-100">{e.source}</span></td>
                    <td className="px-5 py-3 tabular font-medium">{e.entries_count}</td>
                    <td className="px-5 py-3 tabular">${e.purchase_amount?.toFixed(2) || "0.00"}</td>
                    <td className="px-5 py-3">
                      {e.fraud_score >= 50 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                          <AlertTriangle className="size-3" strokeWidth={1.5} /> {e.fraud_score.toFixed(0)}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-500">{e.fraud_score.toFixed(0)}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {(() => {
                        const st = e.email_status;
                        if (!st || st === "disabled") return <span className="text-zinc-400">—</span>;
                        const map = { sent: "bg-emerald-50 text-emerald-700", logged: "bg-blue-50 text-blue-700", failed: "bg-red-50 text-red-700", skipped: "bg-zinc-100 text-zinc-500" };
                        return <span className={`px-2 py-0.5 rounded ${map[st] || "bg-zinc-100 text-zinc-600"}`} data-testid={`email-status-${e.entry_id}`}>{st}</span>;
                      })()}
                    </td>
                    <td className="px-5 py-3 text-xs text-zinc-500">{new Date(e.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leaderboard" && (
          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Rank", "Entrant", "Email", "Entries", "Referrals"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leaderboard.length === 0 && <tr><td colSpan="5" className="px-5 py-10 text-center text-zinc-500 text-sm">No entries yet</td></tr>}
                {leaderboard.map((r) => (
                  <tr key={r.email} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`leaderboard-row-${r.rank}`}>
                    <td className="px-5 py-3 font-display font-bold tabular">{r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}</td>
                    <td className="px-5 py-3 font-medium">{r.name}</td>
                    <td className="px-5 py-3 text-zinc-600">{r.email}</td>
                    <td className="px-5 py-3 tabular font-bold">{r.total_entries}</td>
                    <td className="px-5 py-3 tabular">{r.referrals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "referrals" && (
          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="referrals-tab">
            <div className="p-5 border-b border-zinc-200">
              <h3 className="font-display font-bold text-lg">Referral Performance</h3>
              <p className="text-xs text-zinc-500 mt-1">Each entrant gets a unique referral link. Referrers earn 5 bonus entries per signup.</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Referrer", "Ref Code", "Visits", "Signups", "Bonus Entries", "Link"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-zinc-500 text-sm">No referrals yet — share your public link to start tracking.</td></tr>}
                {referrals.map((r) => (
                  <tr key={r.ref_code} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`referral-row-${r.ref_code}`}>
                    <td className="px-5 py-3 font-medium">{r.email}</td>
                    <td className="px-5 py-3 font-mono text-xs">{r.ref_code}</td>
                    <td className="px-5 py-3 tabular">{r.visits}</td>
                    <td className="px-5 py-3 tabular font-bold">{r.signups}</td>
                    <td className="px-5 py-3 tabular text-emerald-700">+{r.bonus_entries}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/g/${id}?ref=${r.ref_code}`); toast.success("Link copied"); }}
                        className="text-xs text-zinc-600 hover:text-zinc-950 font-medium"
                      >Copy link</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "fraud" && (
          <div className="space-y-4" data-testid="fraud-tab">
            {fraud && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white border border-zinc-200 rounded-md p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-red-600 font-semibold">Flagged (≥50)</div>
                    <div className="font-display text-3xl font-bold tabular mt-2">{fraud.flagged_count}</div>
                  </div>
                  <div className="bg-white border border-zinc-200 rounded-md p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-semibold">Suspicious (1-49)</div>
                    <div className="font-display text-3xl font-bold tabular mt-2">{fraud.suspicious_count}</div>
                  </div>
                  <div className="bg-white border border-zinc-200 rounded-md p-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Auto-Excluded (≥70)</div>
                    <div className="font-display text-3xl font-bold tabular mt-2">{fraud.auto_excluded}</div>
                  </div>
                </div>
                <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
                  <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">High-Risk Entries</div>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-zinc-100"><th className="text-left px-5 py-2 text-[10px] uppercase text-zinc-500">Email</th><th className="text-left px-5 py-2 text-[10px] uppercase text-zinc-500">Source</th><th className="text-left px-5 py-2 text-[10px] uppercase text-zinc-500">Score</th><th></th></tr></thead>
                    <tbody>
                      {[...fraud.flagged, ...fraud.suspicious].length === 0 && <tr><td colSpan="4" className="px-5 py-10 text-center text-zinc-500 text-sm">No flagged entries. Looking clean.</td></tr>}
                      {[...fraud.flagged, ...fraud.suspicious].map((e) => (
                        <tr key={e.entry_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`fraud-row-${e.entry_id}`}>
                          <td className="px-5 py-2.5">{e.email}</td>
                          <td className="px-5 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-zinc-100">{e.source}</span></td>
                          <td className="px-5 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${e.fraud_score >= 70 ? "bg-red-50 text-red-700" : e.fraud_score >= 50 ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{e.fraud_score.toFixed(0)}</span></td>
                          <td className="px-5 py-2.5 text-right space-x-2">
                            <button onClick={async () => { await api.post(`/giveaways/${id}/fraud/action`, { entry_id: e.entry_id, action: "exclude" }); toast.success("Excluded"); const r = await api.get(`/giveaways/${id}/fraud`); setFraud(r.data); }} data-testid={`exclude-${e.entry_id}`} className="text-xs text-red-600 hover:text-red-700 font-medium">Exclude</button>
                            <button onClick={async () => { await api.post(`/giveaways/${id}/fraud/action`, { entry_id: e.entry_id, action: "include" }); toast.success("Cleared"); const r = await api.get(`/giveaways/${id}/fraud`); setFraud(r.data); }} data-testid={`clear-${e.entry_id}`} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">Clear</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "rules" && (
          <div className="bg-white border border-zinc-200 rounded-md p-8">
            <pre className="whitespace-pre-wrap font-mono text-xs text-zinc-700 leading-relaxed">{rules}</pre>
          </div>
        )}

        {tab === "email" && emailCfg && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="entry-email-tab">
            <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-zinc-950">Entry Confirmation Email</h2>
                  <p className="text-xs text-zinc-500 mt-0.5">Auto-sent to customers after a paid Shopify order — once per order.</p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!emailCfg.enabled} onChange={(e) => setEmailCfg({ ...emailCfg, enabled: e.target.checked })} className="accent-emerald-600 size-4" data-testid="email-enabled-toggle" />
                  {emailCfg.enabled ? "On" : "Off"}
                </label>
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Subject</label>
                <input value={emailCfg.subject} onChange={(e) => setEmailCfg({ ...emailCfg, subject: e.target.value })} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="email-subject-input" />
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Body</label>
                <textarea value={emailCfg.body} onChange={(e) => setEmailCfg({ ...emailCfg, body: e.target.value })} rows={12} className="mt-1 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono leading-relaxed" data-testid="email-body-input" />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {emailVars.map((v) => (
                    <button key={v} type="button" onClick={() => setEmailCfg({ ...emailCfg, body: emailCfg.body + `{{${v}}}` })} className="text-[11px] font-mono bg-zinc-100 hover:bg-zinc-200 text-zinc-700 px-2 py-0.5 rounded" data-testid={`email-var-${v}`}>
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={saveEmail} disabled={savingEmail} className="bg-zinc-950 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50" data-testid="save-entry-email">{savingEmail ? "Saving…" : "Save"}</button>
                <button onClick={previewEmail} className="border border-zinc-200 text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-50" data-testid="preview-entry-email">Preview</button>
              </div>

              <div className="border-t border-zinc-100 pt-4">
                <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Send a test email</label>
                <div className="mt-1 flex gap-2">
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-sm" data-testid="email-test-input" />
                  <button onClick={sendTest} className="border border-zinc-200 text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-50" data-testid="send-test-email">Send test</button>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Preview</div>
              <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="email-preview">
                {emailPreview ? (
                  <>
                    <div className="text-xs text-zinc-500">Subject</div>
                    <div className="font-semibold text-zinc-950 mb-3">{emailPreview.subject}</div>
                    <div className="text-xs text-zinc-500">Body</div>
                    <pre className="whitespace-pre-wrap text-sm text-zinc-700 leading-relaxed mt-1">{emailPreview.body}</pre>
                  </>
                ) : (
                  <p className="text-sm text-zinc-400">Click "Preview" to render with sample order data.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "draw" && (
          proLocked ? (
            <UpgradePrompt
              feature="Winner Picker"
              description="Draw a verifiably random, fraud-filtered winner weighted by entries — with a shareable winner reveal. Available on Pro and above."
              testid="draw-upgrade-prompt"
            />
          ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-zinc-200 rounded-md p-8 relative overflow-hidden">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Winner Selection</div>
              <h2 className="font-display text-2xl font-bold mt-2">Draw a random winner</h2>
              <p className="text-sm text-zinc-600 mt-2">Selection is weighted by entries earned. Entries flagged for fraud (score ≥ 70) are excluded.</p>

              <div className="mt-8 bg-zinc-50 border border-zinc-200 rounded-md h-32 flex items-center justify-center overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={reelName + (drawing ? "d" : "s")}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.08 }}
                    className="font-display text-2xl font-bold slot-reel"
                    data-testid="winner-reel"
                  >
                    {reelName}
                  </motion.div>
                </AnimatePresence>
              </div>

              <button
                onClick={drawWinner}
                disabled={drawing}
                data-testid="draw-winner-button"
                className="mt-6 w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="size-4" strokeWidth={1.5} />
                {drawing ? "Drawing…" : "Draw Winner"}
              </button>
            </div>

            <div className="relative">
              {winner ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-emerald-500 text-white rounded-md p-8 relative overflow-hidden"
                  data-testid="winner-card"
                >
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url(https://images.pexels.com/photos/6826275/pexels-photo-6826275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)", backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="relative">
                    <Crown className="size-8" strokeWidth={1.5} />
                    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold mt-4 opacity-90">Winner</div>
                    <div className="font-display text-3xl font-bold mt-2">{winner.winner_name}</div>
                    <div className="text-sm mt-1 opacity-90">{winner.winner_email}</div>
                    {winner.total_pool && <div className="text-xs mt-6 opacity-75">Selected from {winner.total_pool} weighted entries.</div>}
                  </div>
                </motion.div>
              ) : (
                <div className="border border-dashed border-zinc-200 rounded-md p-8 h-full flex items-center justify-center text-center">
                  <div>
                    <Trophy className="size-10 text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
                    <div className="font-display font-semibold text-zinc-700">Winner reveal</div>
                    <div className="text-xs text-zinc-500 mt-1">Click "Draw Winner" to start the suspense.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )
        )}
      </div>
    </Layout>
  );
}
